/* eslint-disable no-underscore-dangle */
/* eslint-disable no-fallthrough */
/* eslint-disable react/button-has-type */
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import {
  Outlet,
  ReactLocation,
  Router,
  useLocation,
  useNavigate,
} from '@tanstack/react-location';
import { Provider } from 'react-redux';
import Cookies from 'js-cookie';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { useIdleTimer } from 'react-idle-timer';
import routes from './pages/routes';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './hooks/redux';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  doesUserTokenExist,
  getUserIdFromToken,
  getUserTokenExpiration,
  GOOGLE_API_KEY,
  loadElasticApm,
  LocalStorageKeys,
  MessageOptions,
  PiPModeOption,
  redirectToLogin,
  toast,
  TOAST_DEFAULT_DURATION,
  TOAST_WARNING_DURATION,
} from './utils';
import ErrorFallback from './pages/ErrorFallback';
import socket from './services/socketService';
import { useGetLogout, useGetUserInfo, usePostRefreshToken } from './queries';
import {
  setMissionFeedbackModalMission,
  toggleIsMissionFeedbackModalVisible,
} from './store/missionFeedbackModalSlice';
import {
  setRequestMissionModalMission,
  toggleIsRequestMissionModalVisible,
  toggleIsConfirmRequestMissionModalVisible,
  setConfirmRequestMissionModalMission,
} from './store/requestMissionSlice';
import { addNewGeoJSON } from './store/socketGeoJSONSlice';
import { toggleIsVersionModalVisible } from './store/versionModalSlice';
import {
  setPrelaunchDrone,
  setPrelaunchMission,
  toggleIsLaunchCheckModalVisible,
} from './store/prelaunchModalsSlice';
import { useCloseAllUtil } from './utils/closeAllUtil';
import { setCurrentPiPMode } from './store/livePageSlice';
import { toggleIsTwoFAEnabled } from './store/twoFAModalSlice';
import { setTokenExpirationDate } from './store/tokenRefreshSlice';
import { setUnreadNotificationCount } from './store/notificationDropdownSlice';
import { clearPendingLaunch, isPendingLaunch } from './utils/launchTracker';
import SocketListeners from './components/SocketListeners';
import { canAcceptScheduledMission } from './utils/userUtilt';

const Notifications = lazy(() => import('./components/Notifications'));
const Navbar = lazy(() => import('./components/Navbar'));
const NotificationDropdown = lazy(
  () => import('./components/NotificationDropdown')
);
const DeleteMissionModal = lazy(
  () => import('./components/DeleteMissionModal')
);
const WeatherModal = lazy(() => import('./components/WeatherModal'));
const EmergencyControls = lazy(() => import('./components/EmergencyControls'));
const ChatModal = lazy(() => import('./components/TeamChat'));
const VersionModal = lazy(() => import('./components/VersionModal'));
const MissionFeedbackModal = lazy(
  () => import('./components/MissionFeedbackModal')
);
const MissionSummaryModal = lazy(
  () => import('./components/MissionSummaryModal')
);
const PrelaunchModals = lazy(() => import('./components/PreLaunchModals'));

const AcceptRequestMissionModal = lazy(
  () => import('./components/AcceptRequestMissionModal')
);

const AcceptConfirmRequestMissionModal = lazy(
  () => import('./components/AcceptConfirmRequestMissionModal')
);

const AddNewMissionModal = lazy(
  () => import('./components/AddNewMissionModal')
);

const ActiveMissionAlertModal = lazy(
  () => import('./components/ActiveMissionAlertModal')
);

function App() {
  const birdsEyeSocketSetup = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userData } = useGetUserInfo();
  const logoutMutation = useGetLogout();
  const userId = getUserIdFromToken();
  const queryClient = useQueryClient();
  // refresh token api
  const { mutate: refreshTokenMutation } = usePostRefreshToken({
    enabled: true,
    keepPreviousData: true,
    retry: 0,
    staleTime: Infinity, // Data will never be marked as stale
    cacheTime: Infinity,
    refetchOnMount: false, // Prevents refetch when component remounts
    refetchOnWindowFocus: false, // Prevents refetch on tab focus
    refetchOnReconnect: false, // Prevents refetch when internet reconnects
  });
  // code for making sure mission request is not overwritten when there are multiple requests
  const confirmRequestedMission = useAppSelector(
    (state) => state.requestMissionModal.isConfirmModalVisible
  );
  const isAcceptRequestedMissionModalOpen = useAppSelector(
    (state) => state.requestMissionModal.isModalVisible
  );

  const tokenExpirationTime = useAppSelector(
    (state) => state.tokenExpirationDate.tokenExpirationTime
  );

  const isHotkeysEnabled = useAppSelector(
    (state) => state.livePage.isHotkeysEnabled
  );

  const acceptRequestedMissionModal = useRef(isAcceptRequestedMissionModalOpen);
  const confirmRequestedMissionModal = useRef(confirmRequestedMission);
  const userDataRef = useRef(userData);
  // keeps track of current state of the two modals wether they are open or not
  useEffect(() => {
    acceptRequestedMissionModal.current = isAcceptRequestedMissionModalOpen;
    confirmRequestedMissionModal.current = confirmRequestedMission;
  }, [confirmRequestedMission, isAcceptRequestedMissionModalOpen]);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const { start: startTimer, pause: pauseTimer } = useIdleTimer({
    onIdle: () => {
      if (
        !userId ||
        !userData?.settings?.inActivity?.enabled ||
        location.current.pathname.includes('live') ||
        location.current.pathname.includes('location')
      )
        return;
      logoutMutation.mutate(
        {
          issuer: null,
          target: userId,
          additionalDetails: {
            logoutType: 'timeout',
            logoutReason: 'inactivity',
            timeoutDuration: userData?.settings?.inActivity?.timeout,
          },
        },
        {
          onSuccess: () => {
            Cookies.remove('chocolateChip');
            localStorage.removeItem(LocalStorageKeys.USER_TOKEN);
            navigate({
              to: '/login',
              replace: true,
            });
          },
        }
      );
    },
    timeout: userData?.settings?.inActivity?.timeout,
    throttle: 500,
    startManually: true,
    startOnMount: false,
  });
  // useEffect to manage inactivity timer
  useEffect(() => {
    if (userId && userData?.settings?.inActivity?.enabled) {
      startTimer();
    } else {
      pauseTimer();
    }
  }, [pauseTimer, startTimer, userData, userId]);

  // useEffect to manage socket connection
  useEffect(() => {
    if (userId) {
      socket.connect();
    } else {
      socket.disconnect();
    }
    return () => {
      socket.disconnect(); // Cleanup socket connection
    };
  }, [userId]);

  // useEffect to emit heartbeat and join/leave chatroom
  useEffect(() => {
    socket.on('connect', () => {
      if (!userId) return;
      socket.emit('join_chatroom', { user_id: userId });
      socket.emit('setup heartbeat', {
        email: userData?.email,
        user_id: userId,
      });
    });
    socket.on('disconnect', () => {
      if (!userId) return;
      socket.emit('leave_chatroom', { user_id: userId });
    });
  }, [userData?.email, userId]);

  // useEffect to display birdseye_alerts
  useEffect(() => {
    // if no loggedin user, return
    if (!userId) return;

    // if socket already setup, return
    if (birdsEyeSocketSetup.current) return;
    birdsEyeSocketSetup.current = true;

    socket.on('connect', () => {
      socket.on(
        'birdseye_alerts',
        (data: {
          missionComplete?: boolean;
          missionHistory?: any;
          alert_type: 'warning' | 'error' | 'simple' | 'success';
          raidAlert?: boolean;
          sent_by?: string;
          timeout?: number;
          message?: string;
          droneOnlineStatus?: boolean;
        }) => {
          const toastDuration = TOAST_DEFAULT_DURATION;
          const toastWarningDuration = TOAST_WARNING_DURATION;
          // for mission complete
          if (data?.missionComplete) {
            dispatch(setCurrentPiPMode(PiPModeOption.NONE));

            // Only show the mission summary and mission feedback popups if the user is on the live page for the mission that ended
            if (
              window?.location?.pathname.includes('live') &&
              data?.missionHistory?.mission?._id ===
                window?.location?.pathname?.split('/')?.[2]
            ) {
              dispatch(toggleIsMissionFeedbackModalVisible(true));
              dispatch(setMissionFeedbackModalMission(data?.missionHistory));
            }

            toast(`${data?.sent_by}: ${data?.message}`, {
              id: `${data?.sent_by}: ${data?.message}`,
              duration: toastDuration,
              position: 'bottom-right',
              type: MessageOptions.SUCCESS,
            });
            return;
          }
          // only for live mission page
          if (window?.location?.pathname.includes('live')) {
            // get current drone for missionId
            const missionId = window?.location?.pathname?.split('/')?.[2];
            const activeDronesList: any = queryClient.getQueryData([
              'activeDrones',
            ]);
            // match mission id and drone launch id to find an active drone name
            const activeDroneQuery = activeDronesList?.find(
              (drone: any) => drone?.launch_id?.mission?._id === missionId
            );
            // check if nickname exists
            const isCurrentDroneNOTActive =
              (activeDroneQuery?.nickname
                ? activeDroneQuery?.nickname
                : activeDroneQuery?.name) !== data?.sent_by;

            // find if alert is sent by a drone
            const dronesList: any = queryClient.getQueryData(['drones']);

            const isSentByADrone =
              typeof dronesList?.find((drone) =>
                drone?.nickname && drone?.nickname !== ''
                  ? drone?.nickname === data?.sent_by
                  : drone?.name === data?.sent_by
              ) !== 'undefined';

            // if message is sent by a drone but not the active drone, return
            if (isCurrentDroneNOTActive && isSentByADrone) return;
          }

          // for updating drone list on drone online/offline status change at a faster rate
          if (data?.droneOnlineStatus) {
            queryClient.invalidateQueries(['drones']);
            return;
          }

          if (data) {
            dispatch(setUnreadNotificationCount());
          }
          // for alerts
          switch (data?.alert_type) {
            case 'error':
              toast(`${data?.sent_by}: ${data?.message}`, {
                id: `${data?.sent_by}: ${data?.message}`,
                duration: Infinity,
                position: 'bottom-right',
                style: {
                  backgroundColor: '#dc2626',
                },
                type: MessageOptions.ERROR,
                icon: 'X',
              });
              break;
            case 'warning':
              toast(`${data?.sent_by}: ${data?.message}`, {
                id: `${data?.sent_by}: ${data?.message}`,
                duration: toastWarningDuration,
                position: 'bottom-right',
                style: {
                  backgroundColor: '#f59e0b',
                },
                type: MessageOptions.WARNING,
                icon: '!!',
              });
              break;
            case 'success':
              toast(`${data?.sent_by}: ${data?.message}`, {
                id: `${data?.sent_by}: ${data?.message}`,
                duration: toastDuration,
                position: 'bottom-right',
                style: {
                  backgroundColor: '#16a34a',
                },
                type: MessageOptions.SUCCESS,
              });
              break;
            default:
              toast(`${data?.sent_by}: ${data?.message}`, {
                id: `${data?.sent_by}: ${data?.message}`,
                duration: toastDuration,
                position: 'bottom-right',
              });
              break;
          }
        }
      );
      // for user commands
      socket.on(
        'birdseye_user_command',
        (data: {
          command?: string;
          commandName?: string;
          message?: string;
          mission_id?: string;
          sent_by?: string;
          user?: string;
          user_id?: string;
        }) => {
          if (userId !== data?.user_id) {
            toast(`${data?.sent_by}: ${data?.message}`, {
              id: `${data?.sent_by}: ${data?.message}`,
              duration: 10000,
              position: 'bottom-right',
            });
          }
        }
      );
    });
    socket.on('user_prompts', (msg) => {
      // if user not logged in
      if (!userId) return;
      // for cases where accept is clicked by a user so then it should close the popup for everone else
      if (msg?.accepted_by !== '') {
        dispatch(toggleIsRequestMissionModalVisible(false));
        dispatch(setRequestMissionModalMission(null));
      }

      // show popup for a requested mission
      if (
        msg?.topic === 'SAQ' &&
        msg?.queuedMission?.state === 'NO_RESOURCES'
      ) {
        // if any of the popup modals are already open then to return and not update anything else
        if (acceptRequestedMissionModal.current === true) {
          return;
        }
        // do not show this popup on live mission page, missions page, or login page
        // not show popup if someone has already clicked accept
        dispatch(setRequestMissionModalMission(msg));
        if (
          window?.location?.pathname.includes('live') ||
          window?.location?.pathname === '/' ||
          window?.location?.pathname.includes(
            'missions' || msg?.accepted_by !== ''
          )
        ) {
          return;
        }
        // console.log('FIRST');
        // console.log(msg)
        // do not show the popup if the user does not have the correct role to accept a mission request
        if (!canAcceptScheduledMission(userDataRef.current?.role?.title)) {
          return;
        }
        dispatch(toggleIsRequestMissionModalVisible(true));
      }

      // show this popup if no drone was selected when requesting a mission
      if (
        msg?.topic === 'SAQ' &&
        msg?.queuedMission?.state === 'READY' &&
        msg?.queuedMission?.droneRequested !== msg?.drone?._id
      ) {
        // if any of the popup modals are already open then to return and not update anything else
        if (confirmRequestedMissionModal.current === true) {
          return;
        }
        if (
          window?.location?.pathname.includes('live') ||
          window?.location?.pathname === '/'
        ) {
          return;
        }
        // console.log('SECOND');
        dispatch(toggleIsRequestMissionModalVisible(false));
        dispatch(setRequestMissionModalMission(null));
        dispatch(toggleIsConfirmRequestMissionModalVisible(true));
        dispatch(setConfirmRequestMissionModalMission(msg));
      }

      if (msg?.msg === 'SHOW_PRETAKEOFF_MODAL') {
        // Backend broadcasts this to every open socket for the PIC. Only the
        // tab that initiated or accepted this mission should open preflight.
        if (!isPendingLaunch(msg?.mission?._id)) {
          return;
        }
        clearPendingLaunch();
        // Ignore if this tab is already viewing a different live mission — prevents
        // a concurrent launch in another tab from yanking this tab onto its mission.
        if (window?.location?.pathname?.includes('live')) {
          const currentMissionId = window?.location?.pathname?.split('/')?.[2];
          if (currentMissionId && currentMissionId !== msg?.mission?._id) {
            return;
          }
        }
        dispatch(setPrelaunchDrone(msg?.drone));
        dispatch(setPrelaunchMission(msg?.mission));
        dispatch(toggleIsRequestMissionModalVisible(false));
        dispatch(setRequestMissionModalMission(null));
        dispatch(toggleIsConfirmRequestMissionModalVisible(false));
        dispatch(setConfirmRequestMissionModalMission(null));
        setTimeout(() => {
          dispatch(toggleIsLaunchCheckModalVisible(true));
        }, 700);
      }
    });

    socket.on('geojson', (msg) => {
      // only show for live page
      if (window?.location?.pathname.includes('live')) {
        const geojsonData = msg.geojson;
        // eslint-disable-next-line no-restricted-syntax
        for (const i in geojsonData.features) {
          if (!geojsonData?.features[i]?.properties?.expirationTimestamp) {
            geojsonData.features[i].properties.expirationTimestamp =
              new Date().getTime() + 10;
          }
        }
        if (msg.geojson.features[0].properties.id) {
          dispatch(addNewGeoJSON(geojsonData));
        }
      }
    });
    socket.on('logout', () => {
      logoutMutation.mutate(
        {
          issuer: null,
          target: userId,
          additionalDetails: {
            logoutType: 'server_initiated',
            logoutReason: 'socket_event',
          },
        },
        {
          onSuccess: () => {
            Cookies.remove('chocolateChip');
            localStorage.removeItem(LocalStorageKeys.USER_TOKEN);
            dispatch(toggleIsTwoFAEnabled(false));
            navigate({
              to: '/login',
              replace: true,
            });
          },
        }
      );
    });
    // eslint-disable-next-line consistent-return
    return () => {
      // socket.off('connect');
      // socket.off('disconnect');
      // socket.off('birdseye_alerts');
      // socket.off('birdseye_user_command');
      // socket.off('geojson');
      // socket.off('logout');
    };
  }, [
    confirmRequestedMission,
    dispatch,
    isAcceptRequestedMissionModalOpen,
    logoutMutation,
    navigate,
    queryClient,
    userData?.role?.title,
    userId,
  ]);

  useEffect(() => {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .addEventListener('change', (event) => {
        // dispatch(toggleIsDarkMode(event.matches));
      });
  }, [dispatch]);

  // set zendesk widget position and hide on mount
  useEffect(() => {
    const path = location.current.pathname;

    // Wait until Zendesk loads
    const interval = setInterval(() => {
      // eslint-disable-next-line prefer-destructuring
      const zE = (window as any).zE;
      if (!zE) return;

      if (path.includes('/login')) {
        // SHOW launcher on login page
        zE('webWidget', 'show');
      } else {
        // HIDE launcher everywhere else
        zE('webWidget', 'hide');
      }

      clearInterval(interval);
    }, 100);

    return () => clearInterval(interval);
  }, [location]);

  const TOKEN_EXPIRY_THRESHOLD = 30 * 60; // 30 minutes in seconds refresh user token if it is going to expire in the next 30 min
  const getRefreshToken = useCallback(() => {
    refreshTokenMutation(
      {}, // No payload needed for refresh
      {
        onSuccess: async ({ data }) => {
          if (data?.error) {
            console.error(data?.error);
          } else {
            await Cookies.set('chocolateChip', data.refreshToken, {
              expires: 8 / 24,
              secure: true,
            });
            const newExpiration = getUserTokenExpiration();
            dispatch(setTokenExpirationDate(newExpiration));
          }
        },
        onError: (error) => {
          console.error('Token refresh failed:', error);
        },
      }
    );
  }, [dispatch, refreshTokenMutation]);

  // Refresh token logic
  useEffect(() => {
    const { pathname } = location.current;

    // PRIORITY: Handle SSO callback first
    const currentUrl = window.location.href;
    const hasSamlAndToken =
      currentUrl.includes('saml') && currentUrl.includes('token');

    if (hasSamlAndToken) {
      const url = new URL(currentUrl);
      const tokenValue = url.searchParams.get('token');
      const redirectPath = url.searchParams.get('redirect') || '/missions';

      if (tokenValue) {
        // Set the cookie
        Cookies.set('chocolateChip', tokenValue, {
          expires: 8 / 24,
          secure: true,
        });

        // Use router navigation for better user experience (no full reload)
        window.location.href = redirectPath;
        return undefined;
      }
    }

    // let the bootstrap code run (don’t force /login yet).
    // Check if token exists
    if (!doesUserTokenExist()) {
      // return undefined to skip refetch token when we are on the login, recover password, or read-only archive page
      const hasTokenInUrl =
        new URLSearchParams(window.location.search).has('token') ||
        window.location.hash.includes('token=');
      if (
        pathname === '/' ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/recover-password') ||
        pathname.startsWith('/saml') ||
        pathname.startsWith('/shared/archive') ||
        hasTokenInUrl
      ) {
        return undefined;
      }

      redirectToLogin(navigate);
      return undefined;
    }

    // If token exists, run the refresh logic.
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const timeLeft = tokenExpirationTime - currentTime;

    // If expiration info is missing or token is expired, redirect to login.
    if (!tokenExpirationTime || timeLeft < 0) {
      redirectToLogin(navigate);
      return undefined;
    }

    // If token is within the refresh threshold, refresh immediately.
    if (timeLeft < TOKEN_EXPIRY_THRESHOLD) {
      getRefreshToken();
      return undefined;
    }

    // Otherwise, calculate how long until the token reaches the threshold.
    const delayUntilThreshold = (timeLeft - TOKEN_EXPIRY_THRESHOLD) * 1000; // convert seconds to ms

    const timeoutId = setTimeout(() => {
      getRefreshToken();
    }, delayUntilThreshold);

    // Cleanup the timeout if dependencies change or component unmounts.
    return () => clearTimeout(timeoutId);
  }, [
    TOKEN_EXPIRY_THRESHOLD,
    tokenExpirationTime,
    getRefreshToken,
    navigate,
    location.current.pathname,
    location,
  ]);

  useEffect(() => {
    loadElasticApm({
      serviceName: 'mission-manager-v5',
      serverUrl:
        'https://2e04855a2691471794bcb9bd3162035d.apm.us-east-1.aws.found.io:443',
      environment: import.meta.env.MODE,
    });
  }, []);

  const handleVersionModalClick = () => {
    dispatch(toggleIsVersionModalVisible(true));
  };

  useCloseAllUtil();

  return (
    <div className="">
      <div className="flex flex-col dark:text-white">
        {
          <div
            className="fixed right-0 top-0 -mt-2 mr-2 pr-2"
            style={{ zIndex: 110 }}
          >
            <button
              type="button"
              onClick={handleVersionModalClick}
              onKeyDown={(e) => {
                // If the user is on the live page, hotkeys are enabled, and the space keyboard button is pressed, don't execute a button click
                if (
                  location.current.pathname.includes('/live') &&
                  isHotkeysEnabled &&
                  e.code === 'Space'
                ) {
                  e.preventDefault();
                }
              }}
              className="text-[10px] text-white"
              data-cy="version"
            >
              Version - {import.meta.env.PACKAGE_VERSION}
            </button>
          </div>
        }
        <Suspense fallback={null}>
          <Notifications />
        </Suspense>
        <Suspense fallback={null}>
          <Navbar />
        </Suspense>
        <div className="h-full px-4 py-8">
          <Suspense fallback={null}>
            <NotificationDropdown />
          </Suspense>
          <Suspense fallback={null}>
            <DeleteMissionModal />
          </Suspense>
          <Suspense fallback={null}>
            <WeatherModal />
          </Suspense>
          <Suspense fallback={null}>
            <EmergencyControls />
          </Suspense>
          <Suspense fallback={null}>
            <ChatModal />
          </Suspense>
          <Suspense fallback={null}>
            <VersionModal />
          </Suspense>
          <Suspense fallback={null}>
            <MissionFeedbackModal />
          </Suspense>
          <Suspense fallback={null}>
            <MissionSummaryModal />
          </Suspense>
          <Suspense fallback={null}>
            <PrelaunchModals />
          </Suspense>
          <Suspense fallback={null}>
            <AcceptRequestMissionModal />
          </Suspense>
          <Suspense>
            <AcceptConfirmRequestMissionModal />
          </Suspense>
          <Suspense fallback={null}>
            <AddNewMissionModal />
          </Suspense>
          <Suspense fallback={null}>
            <ActiveMissionAlertModal />
          </Suspense>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

const wrappedApp = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
      },
    },
  });

  const reactLocation = new ReactLocation();
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router location={reactLocation} routes={routes}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <Helmet>
              <script
                src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&v=3.exp`}
                async
                defer
              />
            </Helmet>
            <SocketListeners />
            <App />
          </Provider>
          <ReactQueryDevtools position="bottom-right" />
        </QueryClientProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default wrappedApp;
